import {
  Shield,
  Lock,
  Server,
  Key,
  ShieldCheck,
  Users,
  FileCheck,
  Eye,
  Globe,
  ArrowRight,
  CheckCircle2,
  Clock,
  Database,
  Network,
} from 'lucide-react';
import { LandingHeader } from '@/components/LandingHeader';
import { LandingFooter } from '@/components/LandingFooter';
import { LocalizedAnchor } from '@/components/LocalizedLink';
import { buttonVariants } from '@/components/ui/Button';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';
import { JsonLd } from '@/components/JsonLd';
import { useT } from '@/i18n/provider';
import { SITE_ORIGIN } from '@/lib/site-config';

function TrustBadge({ icon: Icon, title, status, statusColor }: {
  icon: React.ElementType;
  title: string;
  status: string;
  statusColor: string;
}) {
  return (
    <div className="flex flex-col items-center gap-3 p-6 rounded-2xl bg-gray-900/50 border border-gray-800 hover:border-primary-500/40 transition-colors">
      <div className="w-14 h-14 rounded-xl bg-primary-500/10 flex items-center justify-center">
        <Icon className="h-7 w-7 text-primary-400" />
      </div>
      <h3 className="text-white font-semibold text-sm text-center">{title}</h3>
      <span className={`text-xs font-medium px-3 py-1 rounded-full ${statusColor}`}>
        {status}
      </span>
    </div>
  );
}

function SecurityCard({ icon: Icon, title, items }: {
  icon: React.ElementType;
  title: string;
  items: string[];
}) {
  return (
    <div className="rounded-2xl bg-gray-900/50 border border-gray-800 p-6 hover:border-primary-500/30 transition-colors">
      <div className="flex items-center gap-3 mb-5">
        <div className="w-10 h-10 rounded-lg bg-primary-500/10 flex items-center justify-center flex-shrink-0">
          <Icon className="h-5 w-5 text-primary-400" />
        </div>
        <h3 className="text-lg font-semibold text-white">{title}</h3>
      </div>
      <ul className="space-y-3">
        {items.map((item) => (
          <li key={item} className="flex items-start gap-2.5 text-sm text-gray-400">
            <CheckCircle2 className="h-4 w-4 text-emerald-400 mt-0.5 flex-shrink-0" />
            <span>{item}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

const SUBPROCESSORS = [
  { id: 'railway', name: 'Railway (GCP)' },
  { id: 'clickhouse', name: 'ClickHouse Cloud (GCP)' },
  { id: 'anthropic', name: 'Anthropic' },
  { id: 'azure', name: 'Microsoft Azure' },
  { id: 'cloudflare', name: 'Cloudflare' },
  { id: 'mollie', name: 'Mollie' },
] as const;

export function SecurityPage() {
  const t = useT();

  useDocumentMeta({
    title: t('security.meta.title'),
    description: t('security.meta.description'),
  });

  const webPageJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: t('security.meta.title'),
    description: t('security.meta.description'),
    url: `${SITE_ORIGIN}/security`,
    publisher: {
      '@type': 'Organization',
      name: 'LogPulse',
      url: SITE_ORIGIN,
    },
  };

  return (
    <div className="min-h-screen bg-gray-950">
      <JsonLd data={webPageJsonLd} />
      <LandingHeader />

      {/* Hero */}
      <section className="pt-32 pb-16 sm:pt-40 sm:pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary-500/10 border border-primary-500/20 text-primary-400 text-sm font-medium mb-6">
            <Shield className="h-4 w-4" />
            {t('security.hero.badge')}
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
            {t('security.hero.title')}
          </h1>
          <p className="text-lg sm:text-xl text-gray-400 max-w-3xl mx-auto leading-relaxed">
            {t('security.hero.subtitle')}
          </p>
        </div>
      </section>

      {/* Trust Badges */}
      <section className="pb-16 sm:pb-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <TrustBadge
              icon={ShieldCheck}
              title={t('security.trust.soc2.title')}
              status={t('security.trust.soc2.status')}
              statusColor="bg-amber-500/10 text-amber-400"
            />
            <TrustBadge
              icon={Globe}
              title={t('security.trust.gdpr.title')}
              status={t('security.trust.gdpr.status')}
              statusColor="bg-emerald-500/10 text-emerald-400"
            />
            <TrustBadge
              icon={FileCheck}
              title={t('security.trust.iso.title')}
              status={t('security.trust.iso.status')}
              statusColor="bg-blue-500/10 text-blue-400"
            />
            <TrustBadge
              icon={Lock}
              title={t('security.trust.hipaa.title')}
              status={t('security.trust.hipaa.status')}
              statusColor="bg-emerald-500/10 text-emerald-400"
            />
          </div>
        </div>
      </section>

      {/* Reliability & SLOs */}
      <section className="pb-16 sm:pb-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">{t('security.slo.title')}</h2>
            <p className="text-gray-400 max-w-2xl mx-auto">{t('security.slo.subtitle')}</p>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { metric: t('security.slo.uptime'), target: '99.9%', detail: t('security.slo.uptime.detail') },
              { metric: t('security.slo.queryLatency'), target: '< 200ms', detail: t('security.slo.queryLatency.detail') },
              { metric: t('security.slo.ingestLatency'), target: '< 500ms', detail: t('security.slo.ingestLatency.detail') },
              { metric: t('security.slo.rpo'), target: '< 1h', detail: t('security.slo.rpo.detail') },
            ].map((slo) => (
              <div key={slo.metric} className="rounded-2xl bg-gray-900/50 border border-gray-800 p-6 text-center hover:border-primary-500/30 transition-colors">
                <div className="text-3xl font-bold text-primary-400 mb-2">{slo.target}</div>
                <h3 className="text-white font-semibold text-sm mb-1">{slo.metric}</h3>
                <p className="text-xs text-gray-500">{slo.detail}</p>
              </div>
            ))}
          </div>
          <p className="text-sm text-gray-500 mt-4 text-center">
            {t('security.slo.footer')}{' '}
            <LocalizedAnchor href="/docs/platform/limits" className="text-primary-400 hover:underline">
              {t('security.slo.footerLink')}
            </LocalizedAnchor>
          </p>
        </div>
      </section>

      {/* Infrastructure Security */}
      <section className="py-16 sm:py-20 border-t border-gray-800/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">{t('security.infra.title')}</h2>
            <p className="text-gray-400 max-w-2xl mx-auto">{t('security.infra.subtitle')}</p>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            <SecurityCard
              icon={Server}
              title={t('security.infra.cloud.title')}
              items={[
                t('security.infra.cloud.item1'),
                t('security.infra.cloud.item2'),
                t('security.infra.cloud.item3'),
                t('security.infra.cloud.item4'),
              ]}
            />
            <SecurityCard
              icon={Lock}
              title={t('security.infra.encryption.title')}
              items={[
                t('security.infra.encryption.item1'),
                t('security.infra.encryption.item2'),
                t('security.infra.encryption.item3'),
                t('security.infra.encryption.item4'),
              ]}
            />
          </div>
        </div>
      </section>

      {/* Data Protection */}
      <section className="py-16 sm:py-20 border-t border-gray-800/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">{t('security.data.title')}</h2>
            <p className="text-gray-400 max-w-2xl mx-auto">{t('security.data.subtitle')}</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { id: 'keys', icon: Key },
              { id: 'residency', icon: Globe },
              { id: 'backup', icon: Database },
              { id: 'encryption', icon: Shield },
            ].map(({ id, icon: Icon }) => (
              <div key={id} className="rounded-2xl bg-gray-900/50 border border-gray-800 p-6 text-center hover:border-primary-500/30 transition-colors">
                <Icon className="h-8 w-8 text-primary-400 mx-auto mb-4" />
                <h3 className="text-white font-semibold mb-2">{t(`security.data.${id}.title`)}</h3>
                <p className="text-sm text-gray-400">{t(`security.data.${id}.description`)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Application Security & Access Control */}
      <section className="py-16 sm:py-20 border-t border-gray-800/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-6">
            <SecurityCard
              icon={ShieldCheck}
              title={t('security.app.title')}
              items={[1, 2, 3, 4, 5, 6].map((i) => t(`security.app.item${i}`))}
            />
            <SecurityCard
              icon={Users}
              title={t('security.access.title')}
              items={[1, 2, 3, 4, 5, 6].map((i) => t(`security.access.item${i}`))}
            />
          </div>
        </div>
      </section>

      {/* Compliance */}
      <section className="py-16 sm:py-20 border-t border-gray-800/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">{t('security.compliance.title')}</h2>
            <p className="text-gray-400 max-w-2xl mx-auto">{t('security.compliance.subtitle')}</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {/* GDPR */}
            <div className="rounded-2xl bg-gray-900/50 border border-gray-800 p-6 hover:border-primary-500/30 transition-colors">
              <div className="flex items-center gap-2 mb-4">
                <Globe className="h-5 w-5 text-primary-400" />
                <h3 className="text-white font-semibold">{t('security.compliance.gdpr.title')}</h3>
                <span className="ml-auto text-xs font-medium px-2.5 py-0.5 rounded-full bg-emerald-500/10 text-emerald-400">
                  {t('security.compliance.gdpr.status')}
                </span>
              </div>
              <ul className="space-y-2.5 text-sm text-gray-400">
                {[1, 2, 3, 4].map((i) => (
                  <li key={i} className="flex items-start gap-2">
                    <CheckCircle2 className="h-4 w-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                    <span>{t(`security.compliance.gdpr.item${i}`)}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* SOC 2 */}
            <div className="rounded-2xl bg-gray-900/50 border border-gray-800 p-6 hover:border-primary-500/30 transition-colors">
              <div className="flex items-center gap-2 mb-4">
                <ShieldCheck className="h-5 w-5 text-primary-400" />
                <h3 className="text-white font-semibold">{t('security.compliance.soc2.title')}</h3>
                <span className="ml-auto text-xs font-medium px-2.5 py-0.5 rounded-full bg-amber-500/10 text-amber-400">
                  {t('security.compliance.soc2.status')}
                </span>
              </div>
              <ul className="space-y-2.5 text-sm text-gray-400">
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="h-4 w-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                  <span>{t('security.compliance.soc2.item1')}</span>
                </li>
                <li className="flex items-start gap-2">
                  <Clock className="h-4 w-4 text-amber-400 mt-0.5 flex-shrink-0" />
                  <span>{t('security.compliance.soc2.item2')}</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="h-4 w-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                  <span>{t('security.compliance.soc2.item3')}</span>
                </li>
              </ul>
            </div>

            {/* Retention */}
            <div className="rounded-2xl bg-gray-900/50 border border-gray-800 p-6 hover:border-primary-500/30 transition-colors">
              <div className="flex items-center gap-2 mb-4">
                <FileCheck className="h-5 w-5 text-primary-400" />
                <h3 className="text-white font-semibold">{t('security.compliance.retention.title')}</h3>
                <span className="ml-auto text-xs font-medium px-2.5 py-0.5 rounded-full bg-emerald-500/10 text-emerald-400">
                  {t('security.compliance.retention.status')}
                </span>
              </div>
              <ul className="space-y-2.5 text-sm text-gray-400">
                {[1, 2, 3].map((i) => (
                  <li key={i} className="flex items-start gap-2">
                    <CheckCircle2 className="h-4 w-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                    <span>{t(`security.compliance.retention.item${i}`)}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <p className="text-sm text-gray-500 mt-6 text-center">
            {t('security.compliance.docsLink')}{' '}
            <LocalizedAnchor href="/docs/platform/compliance" className="text-primary-400 hover:underline">
              {t('security.compliance.docsLinkText')}
            </LocalizedAnchor>.
          </p>
        </div>
      </section>

      {/* Operational Security */}
      <section className="py-16 sm:py-20 border-t border-gray-800/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">{t('security.ops.title')}</h2>
            <p className="text-gray-400 max-w-2xl mx-auto">{t('security.ops.subtitle')}</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { id: 'monitoring', icon: Eye },
              { id: 'incident', icon: Shield },
              { id: 'disclosure', icon: ShieldCheck },
              { id: 'updates', icon: Clock },
            ].map(({ id, icon: Icon }) => (
              <div key={id} className="rounded-2xl bg-gray-900/50 border border-gray-800 p-6 text-center hover:border-primary-500/30 transition-colors">
                <Icon className="h-8 w-8 text-primary-400 mx-auto mb-4" />
                <h3 className="text-white font-semibold mb-2">{t(`security.ops.${id}.title`)}</h3>
                <p className="text-sm text-gray-400">{t(`security.ops.${id}.description`)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Data Processing / Sub-processors */}
      <section className="py-16 sm:py-20 border-t border-gray-800/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">{t('security.subprocessors.title')}</h2>
            <p className="text-gray-400 max-w-2xl mx-auto">{t('security.subprocessors.subtitle')}</p>
          </div>
          <div className="max-w-3xl mx-auto">
            <div className="rounded-2xl bg-gray-900/50 border border-gray-800 overflow-hidden">
              <div className="grid grid-cols-3 gap-4 p-4 border-b border-gray-800 text-sm font-semibold text-white bg-gray-900/80">
                <span>{t('security.subprocessors.col.name')}</span>
                <span>{t('security.subprocessors.col.purpose')}</span>
                <span>{t('security.subprocessors.col.location')}</span>
              </div>
              {SUBPROCESSORS.map((proc) => (
                <div key={proc.id} className="grid grid-cols-3 gap-4 p-4 border-b border-gray-800/50 last:border-b-0 text-sm">
                  <span className="text-white font-medium">{proc.name}</span>
                  <span className="text-gray-400">{t(`security.subprocessors.${proc.id}.purpose`)}</span>
                  <span className="text-gray-400">{t(`security.subprocessors.${proc.id}.location`)}</span>
                </div>
              ))}
            </div>
            <p className="text-sm text-gray-500 mt-4 text-center">{t('security.subprocessors.dpa')}</p>
          </div>
        </div>
      </section>

      {/* Contact */}
      <section className="py-16 sm:py-20 border-t border-gray-800/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="max-w-2xl mx-auto rounded-2xl bg-gradient-to-b from-primary-500/10 to-gray-900/50 border border-primary-500/20 p-8 sm:p-12">
            <Network className="h-10 w-10 text-primary-400 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-white mb-4">{t('security.contact.title')}</h2>
            <p className="text-gray-400 mb-6 leading-relaxed">{t('security.contact.subtitle')}</p>
            <a
              href="mailto:security@logpulse.io"
              className={buttonVariants({ variant: 'gradient', size: 'lg' })}
            >
              {t('security.contact.button')}
              <ArrowRight className="h-4 w-4 ml-2" />
            </a>
            <p className="text-sm text-gray-500 mt-4">{t('security.contact.bounty')}</p>
          </div>
        </div>
      </section>

      <LandingFooter />
    </div>
  );
}
